<template>
  <div class="flex w-full h-screen overflow-hidden bg-gray-50">
    <div class="flex-1 w-full overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-6">
        <div class="mb-6 flex shrink-0 items-center justify-between gap-2">
          <div class="">
            <h2 class="text-2xl font-bold flex items-center gap-2">
              Создать витрину
            </h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Формирование новых корзин и букетов</p>
          </div>
          <div class="flex gap-2">
            <Button label="К витринам" severity="info" icon="pi pi-table" outlined @click="router.push('/showcase')" />
            <Button label="Обновить" icon="pi pi-refresh" severity="secondary" outlined @click="fetchData" />
          </div>
        </div>

        <Toast />

        <Card class="min-h-0 flex-1 overflow-hidden">
          <template #title>Новая витрина</template>
          <template #content>
            <div class="grid h-full min-h-0 grid-cols-1 gap-4 xl:grid-cols-3">
              <div class="flex min-h-[36rem] flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div class="mb-3 flex items-center justify-between gap-2">
                  <div>
                    <p class="font-semibold text-slate-900">Товары</p>
                    <p class="text-xs text-slate-500">Выберите товары для новой витрины</p>
                  </div>
                </div>
                <IconField class="mb-2">
                  <InputIcon><i class="pi pi-search" /></InputIcon>
                  <InputText v-model.trim="storeQuery" placeholder="Поиск товара по названию или штрихкоду..." class="w-full" />
                </IconField>

                <div class="min-h-0 flex-1 overflow-auto space-y-2 pr-1">
                  <div v-for="store in filteredStoreItems" :key="store.id" class="rounded-lg border border-gray-200 p-2">
                    <div class="flex items-start justify-between gap-2">
                      <div class="min-w-0">
                        <p class="font-medium truncate">{{ store.name }}</p>
                        <p class="text-xs text-gray-500 truncate">Штрихкод: {{ store.barcode || '—' }}</p>
                        <p class="text-xs mt-1 text-gray-600">Остаток: {{ store.amount ?? 0 }} {{ getUnitLabel(store) }} | Цена: {{ store.price ?? 0 }} ₸</p>
                      </div>
                      <Button
                        size="small"
                        icon="pi pi-plus"
                        label="Добавить"
                        severity="success"
                        @click="addStoreItem(store)"
                        :disabled="isSelected(store.id) || Number(store.amount || 0) <= 0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex min-h-[36rem] flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div class="mb-3">
                  <p class="font-semibold text-slate-900">Позиции новой витрины</p>
                  <p class="text-xs text-slate-500">Состав формируемой витрины</p>
                </div>
                <div v-if="!form.items.length" class="text-sm text-gray-500 py-6 text-center">Добавьте товары из левого списка</div>
                <div v-else class="min-h-0 flex-1 space-y-2 overflow-auto pr-1">
                  <div v-for="item in form.items" :key="item.store_id" class="rounded-lg border border-gray-200 p-2">
                    <div class="flex items-center justify-between gap-2">
                      <p class="font-medium truncate">{{ item.name }}</p>
                      <Button icon="pi pi-trash" severity="danger" text @click="removeSelected(item.store_id)" />
                    </div>
                    <div class="mt-1 text-xs text-gray-600">Остаток: {{ item.amount }} {{ getUnitLabel(item) }} | Цена: {{ item.price }} ₸</div>
                    <div class="mt-2 flex items-center justify-between gap-2">
                      <div class="flex items-center gap-2">
                        <span class="text-sm text-gray-600">Количество:</span>
                        <Button icon="pi pi-minus" severity="secondary" text size="small" @click="decrementItemQuantity(item)" />
                        <InputNumber
                          v-model="item.quantity"
                          :min="1"
                          :max="item.amount || 1"
                          inputClass="w-20"
                          @update:modelValue="clampItemQuantity(item)"
                        />
                        <Button icon="pi pi-plus" severity="secondary" text size="small" @click="incrementItemQuantity(item)" />
                      </div>
                      <div class="text-sm font-semibold">{{ (item.quantity || 0) * (item.price || 0) }} ₸</div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex min-h-[36rem] min-h-0 flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div class="min-h-0 flex-1 overflow-auto pr-1">
                  <div class="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                    <p class="text-xs uppercase tracking-wide text-emerald-700">Общая стоимость витрины</p>
                    <p class="text-2xl font-bold text-emerald-800">{{ totalShowcaseCost }} ₸</p>
                    <p class="mt-1 text-xs text-emerald-700">Товары: {{ productsTotalCost }} ₸ + Тариф: {{ form.order_collection_rate }} ₸</p>
                    <div class="mt-3 rounded-xl border border-emerald-200 bg-white/70 px-3 py-2">
                      <p class="text-xs uppercase tracking-wide text-emerald-700">Возможное кол-во</p>
                      <p class="text-lg font-semibold text-emerald-800">{{ possibleShowcaseQuantity }}</p>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <FloatLabel variant="on">
                      <InputText id="name" v-model.trim="form.name" class="w-full" />
                      <label for="name">Название витрины</label>
                    </FloatLabel>

                    <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p class="mb-3 text-sm font-semibold text-slate-900">Тип продукта</p>
                      <div class="flex flex-wrap gap-3">
                        <label
                          v-for="type in showcaseTypeOptions"
                          :key="type.id"
                          class="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors"
                          :class="form.product_type_id === type.id
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'"
                        >
                          <input
                            type="checkbox"
                            class="h-4 w-4 accent-emerald-600"
                            :checked="form.product_type_id === type.id"
                            @change="toggleProductType(type.id)"
                          >
                          <span>{{ type.name }}</span>
                        </label>
                      </div>
                    </div>

                    <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <p class="mb-3 text-sm font-semibold text-slate-900">Уровень продукта</p>
                      <div class="flex flex-wrap gap-3">
                        <label
                          v-for="level in showcaseLevelOptions"
                          :key="level.id"
                          class="flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition-colors"
                          :class="form.product_level_id === level.id
                            ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'"
                        >
                          <input
                            type="checkbox"
                            class="h-4 w-4 accent-emerald-600"
                            :checked="form.product_level_id === level.id"
                            @change="toggleProductLevel(level.id)"
                          >
                          <span>{{ level.name }}</span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div class="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <p class="mb-3 font-semibold text-slate-900">Фото витрины</p>
                    <div class="flex items-center gap-3">
                      <FileUpload
                        mode="basic"
                        accept="image/*"
                        :maxFileSize="4096000"
                        customUpload
                        auto
                        chooseLabel="Выбрать фото"
                        chooseIcon="pi pi-image"
                        :chooseButtonProps="{
                          severity: 'success',
                          raised: true,
                          class: 'border-emerald-700 bg-emerald-600 text-white hover:bg-emerald-700'
                        }"
                        class="rounded-lg border border-emerald-200 bg-emerald-50 p-2"
                        @select="onSelectImage"
                      />
                      <Button
                        v-if="selectedImageFile"
                        label="Очистить"
                        icon="pi pi-times"
                        severity="danger"
                        text
                        @click="clearImage"
                      />
                    </div>
                    <div v-if="imagePreview" class="mt-3">
                      <img
                        :src="imagePreview"
                        alt="showcase-preview"
                        class="h-36 w-full rounded-xl border object-cover"
                      />
                    </div>
                  </div>
                </div>

                <div class="mt-4 shrink-0 pt-4">
                  <Button label="Создать витрину" icon="pi pi-save" :loading="creating" @click="createShowcase" severity="success" class="w-full" />
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import axios from '../lib/axios'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'

const toast = useToast()
const router = useRouter()

const creating = ref(false)
const storeItems = ref([])
const productTypes = ref([])
const productLevels = ref([])

const storeQuery = ref('')
const selectedImageFile = ref(null)
const imagePreview = ref('')

const form = ref({
  name: '',
  product_type_id: null,
  product_level_id: null,
  order_type_id: null,
  order_collection_rate: 0,
  date: new Date(),
  items: []
})

const filteredStoreItems = computed(() => {
  const availableStoreItems = storeItems.value.filter((item) => Number(item.amount || 0) > 0)
  const q = storeQuery.value.toLowerCase()
  if (!q) return availableStoreItems
  return availableStoreItems.filter((p) => {
    const nameMatch = (p.name || '').toLowerCase().includes(q)
    const barcodeMatch = String(p.barcode || '').toLowerCase().includes(q)
    return nameMatch || barcodeMatch
  })
})

const showcaseTypeOptions = computed(() => {
  return (productTypes.value || []).filter((item) => {
    const name = String(item?.name || '').toLowerCase()
    return name.includes('букет') || name.includes('корз')
  })
})

const showcaseLevelOptions = computed(() => {
  return (productLevels.value || []).filter((item) => {
    const name = String(item?.name || '').toLowerCase()
    return name.includes('прост') || name.includes('сред') || name.includes('слож')
  })
})

const productsTotalCost = computed(() => {
  return form.value.items.reduce((sum, item) => sum + (Number(item.quantity) || 0) * (Number(item.price) || 0), 0)
})

const totalShowcaseCost = computed(() => productsTotalCost.value + (Number(form.value.order_collection_rate) || 0))

const possibleShowcaseQuantity = computed(() => {
  if (!form.value.items.length) {
    return 0
  }

  const limits = form.value.items.map((item) => {
    const amount = Math.max(0, Number(item.amount || 0))
    const quantity = Math.max(1, Number(item.quantity || 1))
    return Math.floor(amount / quantity)
  })

  return Math.min(...limits)
})

watch(
  () => [form.value.product_level_id, form.value.product_type_id],
  async ([levelId, typeId]) => {
    if (!levelId || !typeId) {
      form.value.order_type_id = null
      form.value.order_collection_rate = 0
      return
    }
    await fetchOrderTypeRateInfo(levelId, typeId)
  }
)

const isSelected = (storeId) => form.value.items.some((item) => item.store_id === storeId)

const toggleProductType = (typeId) => {
  form.value.product_type_id = form.value.product_type_id === typeId ? null : typeId
}

const toggleProductLevel = (levelId) => {
  form.value.product_level_id = form.value.product_level_id === levelId ? null : levelId
}

const getUnitLabel = (item) => {
  if (typeof item?.unit === 'string' && item.unit.trim()) return item.unit.trim()
  if (typeof item?.unit_name === 'string' && item.unit_name.trim()) return item.unit_name.trim()
  if (typeof item?.unit?.name === 'string' && item.unit.name.trim()) return item.unit.name.trim()
  if (typeof item?.unit?.label === 'string' && item.unit.label.trim()) return item.unit.label.trim()
  return ''
}

const addStoreItem = (store) => {
  const amount = Number(store.amount || 0)
  if (amount <= 0) {
    toast.add({ severity: 'warn', summary: 'Нет остатка', detail: 'Товар закончился на складе', life: 2500 })
    return
  }
  if (isSelected(store.id)) return

  form.value.items.push({
    store_id: store.id,
    name: store.name,
    amount,
    unit: store.unit ?? store.unit_name ?? store.unit?.name ?? store.unit?.label ?? '',
    price: Number(store.price || 0),
    quantity: 1
  })

  storeQuery.value = ''
}

const removeSelected = (storeId) => {
  form.value.items = form.value.items.filter((item) => item.store_id !== storeId)
}

const clampItemQuantity = (item) => {
  const max = Number(item.amount || 0)
  const safeMax = max > 0 ? max : 1
  const current = Number(item.quantity || 1)
  item.quantity = Math.max(1, Math.min(current, safeMax))
}

const decrementItemQuantity = (item) => {
  item.quantity = Math.max(1, Number(item.quantity || 1) - 1)
  clampItemQuantity(item)
}

const incrementItemQuantity = (item) => {
  item.quantity = Number(item.quantity || 1) + 1
  clampItemQuantity(item)
}

const normalizeItemQuantities = () => {
  form.value.items.forEach((item) => clampItemQuantity(item))
}

const toDateString = (dateValue) => {
  if (!dateValue) return null
  const date = new Date(dateValue)
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

const onSelectImage = (event) => {
  const file = event?.files?.[0]
  if (!file) return
  if (imagePreview.value) {
    URL.revokeObjectURL(imagePreview.value)
  }
  selectedImageFile.value = file
  imagePreview.value = URL.createObjectURL(file)
}

const clearImage = () => {
  if (imagePreview.value) {
    URL.revokeObjectURL(imagePreview.value)
  }
  selectedImageFile.value = null
  imagePreview.value = ''
}

const fetchOrderTypeRateInfo = async (productLevelId, productTypeId) => {
  try {
    const res = await axios.get('/api/get-order-type-rate-info', {
      params: { product_level_id: productLevelId, product_type_id: productTypeId }
    })

    form.value.order_type_id = res.data?.order_type_id ?? null
    form.value.order_collection_rate = res.data?.order_collection_rate ?? 0
  } catch (error) {
    form.value.order_type_id = null
    form.value.order_collection_rate = 0
    toast.add({ severity: 'warn', summary: 'Тариф не найден', detail: error?.response?.data?.message || 'Для выбранных параметров тариф на сегодня не найден', life: 3000 })
  }
}

const fetchData = async () => {
  try {
    const [storeRes, levelsRes, typesRes] = await Promise.all([
      axios.get('/api/store', { params: { price_source: 'daily_only' } }),
      axios.get('/api/get-product-levels'),
      axios.get('/api/get-product-types')
    ])

    storeItems.value = storeRes.data || []
    productLevels.value = levelsRes.data || []
    productTypes.value = typesRes.data || []
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось загрузить данные', life: 3000 })
  }
}

const createShowcase = async () => {
  if (!form.value.name) {
    toast.add({ severity: 'warn', summary: 'Внимание', detail: 'Введите название витрины', life: 3000 })
    return
  }

  if (!form.value.order_type_id) {
    toast.add({ severity: 'warn', summary: 'Внимание', detail: 'Выберите тип и уровень продукта', life: 3000 })
    return
  }

  normalizeItemQuantities()

  if (form.value.items.some((item) => Number(item.quantity) > Number(item.amount || 0))) {
    toast.add({ severity: 'warn', summary: 'Ошибка количества', detail: 'Количество не может быть больше остатка', life: 3000 })
    return
  }

  creating.value = true
  try {
    const formData = new FormData()
    formData.append('name', form.value.name)
    formData.append('order_type_id', String(form.value.order_type_id))
    formData.append('order_collection_rate', String(form.value.order_collection_rate || 0))
    formData.append('date', toDateString(form.value.date) || '')
    formData.append('quantity', String(possibleShowcaseQuantity.value))

    form.value.items.forEach((item, index) => {
      formData.append(`items[${index}][store_id]`, String(item.store_id))
      formData.append(`items[${index}][quantity]`, String(item.quantity || 1))
    })

    if (selectedImageFile.value) {
      formData.append('image', selectedImageFile.value)
    }

    await axios.post('/api/showcases', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    toast.add({ severity: 'success', summary: 'Успех', detail: 'Витрина создана', life: 3000 })
    form.value = {
      name: '',
      product_type_id: null,
      product_level_id: null,
      order_type_id: null,
      order_collection_rate: 0,
      date: new Date(),
      items: []
    }
    clearImage()
    storeQuery.value = ''
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: error?.response?.data?.message || 'Не удалось создать витрину', life: 3000 })
  } finally {
    creating.value = false
  }
}

onMounted(fetchData)
</script>

<style scoped>
:deep(.p-card),
:deep(.p-card-body),
:deep(.p-card-content) {
  height: 100%;
}

:deep(.p-card-body),
:deep(.p-card-content) {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
}
</style>
